import { alpha, type SxProps, type Theme } from '@mui/material';

export const dataFormStyles = {
  tableToolbar: (theme: Theme) => ({
    pl: { sm: theme.spacing(2) },
    pr: { xs: theme.spacing(1), sm: theme.spacing(1) },
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  }),
  tableToolbarLeft: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }),
  emptyCell: (theme: Theme) => ({
    py: theme.spacing(3),
    color: theme.palette.text.secondary,
  }),
  headerInline: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
  }),
  headerInfoIcon: (theme: Theme) => ({
    fontSize: 15,
    color: theme.palette.grey.infoIconGrey,
    cursor: 'help',
    flexShrink: 0,
  }),
  headerControlButton: (isActive: boolean) => (theme: Theme) => ({
    color: isActive ? theme.palette.primary.main : theme.palette.grey.infoIconGrey,
    p: 0.25,
    ml: 0.25,
  }),
  filterIcon: (isActive: boolean) => ({
    color: isActive ? 'primary.main' : 'text.secondary',
  }),
  filterMenuPaper: () => ({
    width: 320,
    maxWidth: '100%',
  }),
  filterMenuItem: (theme: Theme) => ({
    py: theme.spacing(1),
    px: theme.spacing(2),
  }),
  dialogSection: {
    '& .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiDialogActions-root': {
      backgroundColor: 'common.white',
    },
  } as SxProps<Theme>,
  dialogLoader: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'center',
    py: theme.spacing(3),
  }),
  dialogListItem: (theme: Theme) => ({
    display: 'block',
    py: theme.spacing(0.5),
  }),
  dialogListBtn: {
    borderRadius: 2,
    alignItems: 'flex-start',
    backgroundColor: '#fff',
  } as SxProps<Theme>,
  dialogListIcon: (theme: Theme) => ({
    minWidth: 40,
    mt: theme.spacing(0.25),
  }),
  dialogDetails: (theme: Theme) => ({
    px: theme.spacing(7),
    pb: theme.spacing(1.5),
    pt: theme.spacing(0.5),
  }),
  dialogDetailsRow: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }),
  dialogPagination: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'center',
    mt: theme.spacing(2),
  }),
  modalActions: (theme: Theme) => ({
    px: theme.spacing(2),
    py: theme.spacing(1.5),
  }),
  documentTableSection: {
    backgroundColor: 'transparent',
  } as SxProps<Theme>,
  documentTableToolbar: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    mb: theme.spacing(2),
  }),
  documentTableToolbarTitle: {
    fontSize: '2rem',
    lineHeight: 1.2,
    fontWeight: 700,
    color: 'text.primary',
  } as SxProps<Theme>,
  documentPrimaryAction: (theme: Theme) => ({
    minHeight: 44,
    padding: '10px 24px',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    lineHeight: 1.2,
    fontWeight: 500,
    textTransform: 'none',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    color: theme.palette.common.white,
  }),
  documentTableContainer: (theme: Theme) => ({
    border: `1px solid ${theme.palette.border.subtle}`,
    borderRadius: '12px',
    backgroundColor: theme.palette.common.white,
    overflowX: 'auto',
  }),
  documentTable: {
    minWidth: 980,
    borderCollapse: 'separate',
    borderSpacing: 0,
  } as SxProps<Theme>,
  documentTableHead: (theme: Theme) => ({
    backgroundColor: theme.palette.background.tableHeader,
  }),
  documentTableHeadCell: (theme: Theme, columnKey: string) => ({
    py: 1.75,
    px: 2.5,
    borderBottom: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: theme.palette.background.tableHeader,
    color: theme.palette.text.primary,
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '14px',
    lineHeight: '14px',
    fontWeight: 800,
    letterSpacing: 0,
    whiteSpace: 'nowrap',
    ...(columnKey === 'document' ? { width: '36%' } : {}),
    ...(columnKey === 'name' ? { width: '34%' } : {}),
    ...(columnKey === 'createdAt' ? { width: '28%' } : {}),
    ...(columnKey === 'period' ? { width: '18%' } : {}),
    ...(columnKey === 'deadline' ? { width: '17%' } : {}),
    ...(columnKey === 'status' ? { width: '20%' } : {}),
    ...(columnKey === 'action' ? { width: '17%' } : {}),
    ...(columnKey === 'actions' ? { width: '18%' } : {}),
  }),
  documentTableBodyCell: (theme: Theme, columnKey: string) => ({
    py: 2.25,
    px: 2.5,
    borderBottom: `1px solid ${alpha(theme.palette.border.subtle, 0.8)}`,
    verticalAlign: 'middle',
    color: theme.palette.text.primary,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    ...(columnKey === 'document'
      ? {
          fontWeight: 400,
          color: theme.palette.text.primary,
        }
      : {}),
    ...(columnKey === 'actions' || columnKey === 'action'
      ? {
          whiteSpace: 'nowrap',
        }
      : {}),
  }),
  documentTableRow: (theme: Theme) => ({
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
    '&.MuiTableRow-hover:hover': {
      backgroundColor: theme.palette.common.white,
    },
    '&:last-of-type td': {
      borderBottom: 0,
    },
  }),
  documentEmptyCell: (theme: Theme) => ({
    py: theme.spacing(4),
    px: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    borderBottom: 0,
  }),
} as const;


